import { FC, useEffect, useRef, useState } from "react";

import { Stack } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

import { useAssistantConfig } from "../contexts/AssistantConfigContext";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardBaseInput } from "@/dashboard-design-system/BaseInput/DashboardBaseInput";
import { CropSquare, SendDiagonal } from "@/icons/outlined";

const MAX_ROWS = 14;
const SINGLE_LINE_HEIGHT = 30;

interface ComposerProps {
  value: string;
  isDisabled: boolean;
  onStop?: () => void;
  onChange: (value: string) => void;
  onSubmit: () => void;
}

export const Composer: FC<ComposerProps> = ({ value, isDisabled, onStop, onChange, onSubmit }) => {
  const { t } = useTranslate();
  const { feature } = useAssistantConfig();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const shouldRefocus = useRef(false);
  const [hasWrapped, setHasWrapped] = useState(false);
  const isMultiline = Boolean(value) && hasWrapped;
  useEffect(() => {
    if (!isDisabled && shouldRefocus.current) {
      shouldRefocus.current = false;
      if (!document.activeElement || document.activeElement === document.body) {
        inputRef.current?.focus();
      }
    }
  }, [isDisabled]);
  const submit = () => {
    if (!isDisabled && value.trim()) {
      shouldRefocus.current = true;
      onSubmit();
    }
  };
  return (
    <Stack
      component="form"
      sx={{ position: "relative" }}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <DashboardBaseInput
        inputRef={inputRef}
        disabled={isDisabled}
        multiline
        maxRows={MAX_ROWS}
        value={value}
        placeholder={t("dashboard", "assistant.composer.placeholder")}
        onChange={(event) => {
          setHasWrapped(event.target.scrollHeight > SINGLE_LINE_HEIGHT);
          onChange(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            submit();
          }
        }}
        inputProps={{
          "aria-label": t("dashboard", "assistant.composer.placeholder"),
          "data-test": `${feature}-input`,
          maxLength: 2000,
        }}
        sx={(theme) => ({
          borderRadius: theme.radius(isMultiline ? 2 : 4),
          "&.MuiInputBase-multiline": { py: 1.5, pl: 1.5, pr: 7 },
          "& .MuiInputBase-input": { px: 0, py: 0.125 },
          "&:focus-within": {
            borderColor: theme.palette.border.brand.intense,
            boxShadow: `0 0 0 3px ${theme.palette.border.brand.moderate}`,
          },
          "&.Mui-disabled": {
            bgcolor: theme.palette.surface.neutral.supershy,
            borderColor: theme.palette.border.neutral.quiet,
            opacity: 1,
          },
        })}
      />
      {onStop ? (
        <DashboardIconButton
          type="button"
          variant="filled"
          vibe="neutral"
          aria-label={t("dashboard", "assistant.composer.stop")}
          data-test={`${feature}-stop`}
          sx={getActionButtonSx}
          onClick={onStop}
        >
          <CropSquare />
        </DashboardIconButton>
      ) : (
        <DashboardIconButton
          type="submit"
          variant="filled"
          disabled={isDisabled || !value.trim()}
          aria-label={t("dashboard", "assistant.composer.send")}
          data-test={`${feature}-send`}
          sx={getActionButtonSx}
        >
          <SendDiagonal />
        </DashboardIconButton>
      )}
    </Stack>
  );
};

const getActionButtonSx: SxProps<Theme> = (theme) => ({
  position: "absolute",
  right: theme.spacing(0.75),
  bottom: theme.spacing(0.75),
  borderRadius: "50%",
});
